/**
 * Window presets for the Availability report — which months the grid shows.
 *
 * Every preset resolves from an injected `today` (`"YYYY-MM-DD"`), never the
 * clock, so the same inputs always produce the same months. Quarters and the
 * fiscal year are calendar-based (Jan–Dec).
 *
 * 🔒 FIREWALL: HOURS only. No Fit Score, Capacity, Intensity, or points imports.
 */
import {
  addMonths,
  isMonthStart,
  monthDiff,
  monthLabel,
  monthRange,
  monthStartOf,
  parseDay,
} from "@/lib/availability/months";

export type WindowPreset =
  | "next90"
  | "thisQuarter"
  | "nextQuarter"
  | "next6"
  | "rolling12"
  | "fiscalYear"
  | "ytd"
  | "custom";

/** The longest custom window. A longer range is truncated to this many months from `from`. */
export const CUSTOM_WINDOW_MAX_MONTHS = 24;

export interface CustomWindowInput {
  /** Any day in the first month; normalised to its month start. */
  from: string;
  /** Any day in the last month, inclusive; normalised to its month start. */
  to: string;
}

export interface ResolvedWindow {
  /** Ordered month starts, `"YYYY-MM-01"`. */
  months: string[];
  /** `"Oct 2026 – Dec 2026"`, or `"Oct 2026"` for a one-month window. */
  label: string;
}

const DAY_OR_MONTH = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * Why a custom range is unusable, or null when it is fine. A range longer than
 * {@link CUSTOM_WINDOW_MAX_MONTHS} is NOT an error — it is capped.
 */
export function validateCustomWindow(custom: CustomWindowInput | undefined): string | null {
  if (custom === undefined) return "A custom window needs a from and a to month";
  if (!DAY_OR_MONTH.test(custom.from) || !DAY_OR_MONTH.test(custom.to)) {
    return "From and to must be dates in YYYY-MM-DD form";
  }
  if (monthStartOf(custom.from) > monthStartOf(custom.to)) {
    return "From must be on or before to";
  }
  return null;
}

/** Label for an ordered month list. Empty string for an empty list. */
export function windowLabel(months: readonly string[]): string {
  const first = months[0];
  const last = months[months.length - 1];
  if (first === undefined || last === undefined) return "";
  if (first === last) return monthLabel(first);
  return `${monthLabel(first)} – ${monthLabel(last)}`;
}

function toWindow(months: string[]): ResolvedWindow {
  return { months, label: windowLabel(months) };
}

/** First month of the calendar quarter containing `month`. */
function quarterStart(month: string): string {
  const monthIndex = Number(month.slice(5, 7)) - 1;
  return addMonths(month, -(monthIndex % 3));
}

/**
 * Resolve a preset to its months. Throws a RangeError for a malformed `today`
 * or an invalid custom range (see {@link validateCustomWindow}).
 */
export function resolveWindow(
  preset: WindowPreset,
  today: string,
  custom?: CustomWindowInput,
): ResolvedWindow {
  const { year } = parseDay(today);
  const current = monthStartOf(today);
  const january = `${year}-01-01`;

  switch (preset) {
    case "next90":
      return toWindow(monthRange(current, 3));
    case "thisQuarter":
      return toWindow(monthRange(quarterStart(current), 3));
    case "nextQuarter":
      return toWindow(monthRange(addMonths(quarterStart(current), 3), 3));
    case "next6":
      return toWindow(monthRange(current, 6));
    case "rolling12":
      return toWindow(monthRange(current, 12));
    case "fiscalYear":
      return toWindow(monthRange(january, 12));
    case "ytd":
      return toWindow(monthRange(january, monthDiff(january, current) + 1));
    case "custom": {
      const problem = validateCustomWindow(custom);
      if (problem !== null || custom === undefined) {
        throw new RangeError(problem ?? "A custom window needs a from and a to month");
      }
      const from = monthStartOf(custom.from);
      const to = monthStartOf(custom.to);
      const length = Math.min(monthDiff(from, to) + 1, CUSTOM_WINDOW_MAX_MONTHS);
      return toWindow(monthRange(from, length));
    }
  }
}

/**
 * Step a window back (-1) or forward (+1) by its own length, so "next 3 months"
 * steps to the 3 months after it. An empty or malformed list steps to empty.
 */
export function stepWindow(months: readonly string[], direction: -1 | 1): ResolvedWindow {
  if (!months.every(isMonthStart)) return toWindow([]);
  const shift = months.length * direction;
  return toWindow(months.map((m) => addMonths(m, shift)));
}
